import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaArrowLeft, FaCode, FaEye, FaPaperPlane, FaPercentage, FaSpinner } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import emailApi, { EmailStockItem } from '../../../services/emailApi.ts';
import './AdminEmailPage.css';

type RecipientType = 'ALL' | 'PREMIUM' | 'FREE';

interface PendingAction {
    type: 'new' | 'single' | 'bulk' | 'noStocks';
    stockId?: number;
    stockIds?: number[];
}

const PAGE_SIZE = 20;

// ── Recipient Popup ──────────────────────────────────────────────────────────

const RecipientModal: React.FC<{
    onConfirm: (type: RecipientType) => void;
    onCancel: () => void;
    sending: boolean;
}> = ({ onConfirm, onCancel, sending }) => {
    const [selected, setSelected] = useState<RecipientType>('ALL');

    return (
        <div className="ae-modal-overlay" onClick={onCancel}>
            <div className="ae-modal" onClick={e => e.stopPropagation()}>
                <h3 className="ae-modal-title">Select Recipients</h3>
                <p className="ae-modal-desc">Who should receive this email?</p>

                <div className="ae-radio-group">
                    {([
                        { value: 'ALL',     label: 'All Users',     desc: 'Every registered user' },
                        { value: 'PREMIUM', label: 'Premium Users', desc: 'Users with active subscription' },
                        { value: 'FREE',    label: 'Free Users',    desc: 'Users without subscription' },
                    ] as { value: RecipientType; label: string; desc: string }[]).map(opt => (
                        <label
                            key={opt.value}
                            className={`ae-radio-option ${selected === opt.value ? 'selected' : ''}`}
                            onClick={() => setSelected(opt.value)}
                        >
                            <input
                                type="radio"
                                name="recipientType"
                                value={opt.value}
                                checked={selected === opt.value}
                                onChange={() => setSelected(opt.value)}
                            />
                            <div className="ae-radio-text">
                                <span className="ae-radio-label">{opt.label}</span>
                                <span className="ae-radio-desc">{opt.desc}</span>
                            </div>
                        </label>
                    ))}
                </div>

                <div className="ae-modal-footer">
                    <button className="ae-modal-cancel" onClick={onCancel} disabled={sending}>
                        Cancel
                    </button>
                    <button
                        className="ae-send-btn"
                        onClick={() => onConfirm(selected)}
                        disabled={sending}
                    >
                        {sending ? <FaSpinner className="ae-spin" /> : <FaPaperPlane />}
                        {sending ? 'Sending...' : 'Send Email'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Single-Day Movement % Popup ───────────────────────────────────────────────

const MovementModal: React.FC<{
    stock: EmailStockItem;
    onConfirm: (percent: number, type: RecipientType) => void;
    onCancel: () => void;
    sending: boolean;
}> = ({ stock, onConfirm, onCancel, sending }) => {
    const [percent, setPercent] = useState('');
    const [selected, setSelected] = useState<RecipientType>('ALL');

    return (
        <div className="ae-modal-overlay" onClick={onCancel}>
            <div className="ae-modal" onClick={e => e.stopPropagation()}>
                <h3 className="ae-modal-title">Send Movement Email</h3>
                <p className="ae-modal-desc">
                    <strong>{stock.symbol}</strong> &middot; {stock.stockName}
                    {stock.screenerName && <> &middot; {stock.screenerName}</>}
                </p>

                <div className="ae-form-group">
                    <label className="ae-form-label">Single-Day Movement (%)</label>
                    <input
                        className="ae-search"
                        type="number"
                        step="0.01"
                        placeholder="e.g., 8 or 10"
                        value={percent}
                        onChange={e => setPercent(e.target.value)}
                        autoFocus
                    />
                </div>

                <div className="ae-radio-group">
                    {([
                        { value: 'ALL',     label: 'All Users',     desc: 'Every registered user' },
                        { value: 'PREMIUM', label: 'Premium Users', desc: 'Users with active subscription' },
                        { value: 'FREE',    label: 'Free Users',    desc: 'Users without subscription' },
                    ] as { value: RecipientType; label: string; desc: string }[]).map(opt => (
                        <label
                            key={opt.value}
                            className={`ae-radio-option ${selected === opt.value ? 'selected' : ''}`}
                            onClick={() => setSelected(opt.value)}
                        >
                            <input
                                type="radio"
                                name="movementRecipientType"
                                value={opt.value}
                                checked={selected === opt.value}
                                onChange={() => setSelected(opt.value)}
                            />
                            <div className="ae-radio-text">
                                <span className="ae-radio-label">{opt.label}</span>
                                <span className="ae-radio-desc">{opt.desc}</span>
                            </div>
                        </label>
                    ))}
                </div>

                <div className="ae-modal-footer">
                    <button className="ae-modal-cancel" onClick={onCancel} disabled={sending}>
                        Cancel
                    </button>
                    <button
                        className="ae-send-btn"
                        onClick={() => onConfirm(parseFloat(percent), selected)}
                        disabled={sending || !percent}
                    >
                        {sending ? <FaSpinner className="ae-spin" /> : <FaPaperPlane />}
                        {sending ? 'Sending...' : 'Send Email'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Custom Email Section ──────────────────────────────────────────────────────

const RECIPIENT_OPTS: { value: RecipientType; label: string; desc: string }[] = [
    { value: 'ALL',     label: 'All Users',     desc: 'Every registered user' },
    { value: 'PREMIUM', label: 'Premium',        desc: 'Active subscribers only' },
    { value: 'FREE',    label: 'Free Users',     desc: 'Users without subscription' },
];

const CustomEmailSection: React.FC = () => {
    const [subject,       setSubject]       = useState('');
    const [content,       setContent]       = useState('');
    const [contentType,   setContentType]   = useState<'HTML' | 'TEXT'>('HTML');
    const [recipientType, setRecipientType] = useState<RecipientType>('ALL');
    const [showPreview,   setShowPreview]   = useState(true);
    const [sending,       setSending]       = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Wrap plain text preview in a styled container
    const previewHtml = contentType === 'HTML'
        ? content
        : `<div style="font-family:Arial,sans-serif;font-size:14px;color:#222;line-height:1.7;white-space:pre-wrap;padding:16px;">${
              content.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
          }</div>`;

    const handleSend = async () => {
        if (!subject.trim()) { toast.error('Subject is required'); return; }
        if (!content.trim()) { toast.error('Content is required'); return; }
        setSending(true);
        try {
            const msg = await emailApi.sendCustomEmail(subject, content, contentType, recipientType);
            toast.success(msg);
        } catch (e: any) {
            toast.error(e.response?.data?.message || 'Failed to send email');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="ae-card ae-custom-card">
            <div className="ae-card-header">
                <span className="ae-card-icon">✍️</span>
                <div>
                    <h3 className="ae-card-title">Custom Email</h3>
                    <p className="ae-card-desc">Write your own email in HTML or plain text and send it to any audience.</p>
                </div>
            </div>

            {/* Subject */}
            <div className="ae-custom-field">
                <label className="ae-custom-label">Subject</label>
                <input
                    className="ae-custom-input"
                    type="text"
                    placeholder="Enter email subject..."
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    maxLength={255}
                />
            </div>

            {/* Content type + preview toggle row */}
            <div className="ae-custom-toolbar">
                <div className="ae-type-toggle">
                    <button
                        className={`ae-type-btn ${contentType === 'HTML' ? 'active' : ''}`}
                        onClick={() => setContentType('HTML')}
                    >
                        <FaCode /> HTML
                    </button>
                    <button
                        className={`ae-type-btn ${contentType === 'TEXT' ? 'active' : ''}`}
                        onClick={() => setContentType('TEXT')}
                    >
                        Plain Text
                    </button>
                </div>

                {contentType === 'HTML' && (
                    <button
                        className={`ae-preview-toggle ${showPreview ? 'active' : ''}`}
                        onClick={() => setShowPreview(p => !p)}
                        title={showPreview ? 'Hide preview' : 'Show preview'}
                    >
                        <FaEye /> {showPreview ? 'Hide Preview' : 'Show Preview'}
                    </button>
                )}
            </div>

            {/* Editor + Preview split */}
            <div className={`ae-editor-wrap ${contentType === 'HTML' && showPreview ? 'split' : 'full'}`}>
                <div className="ae-editor-pane">
                    <div className="ae-pane-label">
                        {contentType === 'HTML' ? 'HTML Editor' : 'Text Content'}
                    </div>
                    <textarea
                        className="ae-editor"
                        placeholder={contentType === 'HTML'
                            ? '<h1>Hello {{name}}</h1>\n<p>Your message here...</p>'
                            : 'Write your plain text email here...'
                        }
                        value={content}
                        onChange={e => setContent(e.target.value)}
                        spellCheck={contentType === 'TEXT'}
                    />
                </div>

                {contentType === 'HTML' && showPreview && (
                    <div className="ae-preview-pane">
                        <div className="ae-pane-label">
                            Preview
                            <span className="ae-pane-label-hint">renders as recipients will see it</span>
                        </div>
                        <iframe
                            ref={iframeRef}
                            className="ae-preview-iframe"
                            srcDoc={previewHtml || '<div style="font-family:Arial;color:#aaa;padding:24px;font-size:13px;">Start typing HTML to see a preview...</div>'}
                            sandbox="allow-same-origin"
                            title="Email Preview"
                        />
                    </div>
                )}
            </div>

            {/* Recipients */}
            <div className="ae-custom-field">
                <label className="ae-custom-label">Send To</label>
                <div className="ae-custom-recipients">
                    {RECIPIENT_OPTS.map(opt => (
                        <label
                            key={opt.value}
                            className={`ae-recipient-chip ${recipientType === opt.value ? 'active' : ''}`}
                            onClick={() => setRecipientType(opt.value)}
                        >
                            <input
                                type="radio"
                                name="customRecipient"
                                value={opt.value}
                                checked={recipientType === opt.value}
                                onChange={() => setRecipientType(opt.value)}
                            />
                            <span className="ae-chip-label">{opt.label}</span>
                            <span className="ae-chip-desc">{opt.desc}</span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Send */}
            <div className="ae-custom-footer">
                <button
                    className="ae-send-btn ae-send-custom"
                    onClick={handleSend}
                    disabled={sending || !subject.trim() || !content.trim()}
                >
                    {sending ? <FaSpinner className="ae-spin" /> : <FaPaperPlane />}
                    {sending ? 'Sending...' : `Send to ${RECIPIENT_OPTS.find(o => o.value === recipientType)?.label}`}
                </button>
                {!sending && (
                    <span className="ae-custom-footer-hint">
                        This will send a real email to all {recipientType.toLowerCase() === 'all' ? '' : recipientType.toLowerCase() + ' '}users immediately.
                    </span>
                )}
            </div>
        </div>
    );
};

// ── Main Page ────────────────────────────────────────────────────────────────

const AdminEmailPage: React.FC = () => {
    const navigate = useNavigate();

    const [allStocks, setAllStocks] = useState<EmailStockItem[]>([]);
    const [stocksLoading, setStocksLoading] = useState(true);

    const [screenerFilter, setScreenerFilter] = useState<string>('ALL');
    const [bulkSearch, setBulkSearch] = useState('');
    const [bulkPage, setBulkPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

    // Modal state
    const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
    const [sending, setSending] = useState(false);

    // Movement % modal state
    const [movementTarget, setMovementTarget] = useState<EmailStockItem | null>(null);
    const [movementSending, setMovementSending] = useState(false);

    useEffect(() => {
        emailApi.getAllStocks()
            .then(setAllStocks)
            .catch(() => toast.error('Failed to load stocks'))
            .finally(() => setStocksLoading(false));
    }, []);

    const screenerOptions = useMemo(() => {
        const names = [...new Set(allStocks.map(s => s.screenerName).filter(Boolean))] as string[];
        return names.sort();
    }, [allStocks]);

    const filteredStocks = useMemo(() => {
        let list = allStocks;
        if (screenerFilter !== 'ALL') list = list.filter(s => s.screenerName === screenerFilter);
        if (bulkSearch.trim()) {
            const q = bulkSearch.trim().toLowerCase();
            list = list.filter(s =>
                s.symbol.toLowerCase().includes(q) || s.stockName.toLowerCase().includes(q)
            );
        }
        return list;
    }, [allStocks, screenerFilter, bulkSearch]);

    const totalPages = Math.ceil(filteredStocks.length / PAGE_SIZE);
    const pagedStocks = filteredStocks.slice((bulkPage - 1) * PAGE_SIZE, bulkPage * PAGE_SIZE);
    const allPageSelected = pagedStocks.length > 0 && pagedStocks.every(s => selectedIds.has(s.id));

    const toggleSelectAll = () => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (allPageSelected) pagedStocks.forEach(s => next.delete(s.id));
            else pagedStocks.forEach(s => next.add(s.id));
            return next;
        });
    };

    const toggleStock = (id: number) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const formatDate = (d: string) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const formatPrice = (p: number) => `₹${p.toFixed(2)}`;
    const formatUpside = (u: number | null) => {
        if (u == null) return <span className="ae-na">Tracking...</span>;
        return <span className={u >= 0 ? 'ae-profit' : 'ae-loss'}>{u >= 0 ? '+' : ''}{u.toFixed(2)}%</span>;
    };

    // ── Open modal ────────────────────────────────────────────────────────────

    const openModal = (action: PendingAction) => setPendingAction(action);

    // ── Confirm send after modal ──────────────────────────────────────────────

    const handleConfirm = async (recipientType: RecipientType) => {
        if (!pendingAction) return;
        setSending(true);
        try {
            let msg = '';
            if (pendingAction.type === 'new') {
                msg = await emailApi.sendNewStocksEmail(recipientType);
            } else if (pendingAction.type === 'noStocks') {
                msg = await emailApi.sendNoStocksTodayEmail(recipientType);
            } else if (pendingAction.type === 'single' && pendingAction.stockId) {
                msg = await emailApi.sendSingleStockEmail(pendingAction.stockId, recipientType);
            } else if (pendingAction.type === 'bulk' && pendingAction.stockIds) {
                msg = await emailApi.sendBulkStocksEmail(pendingAction.stockIds, recipientType);
                setSelectedIds(new Set());
            }
            toast.success(msg);
            setPendingAction(null);
        } catch (e: any) {
            toast.error(e.response?.data?.message || 'Failed to send email');
        } finally {
            setSending(false);
        }
    };

    // ── Confirm send for movement % email ─────────────────────────────────────

    const handleMovementConfirm = async (percent: number, recipientType: RecipientType) => {
        if (!movementTarget || isNaN(percent)) return;
        setMovementSending(true);
        try {
            const msg = await emailApi.sendSingleDayMovementEmail(movementTarget.id, percent, recipientType);
            toast.success(msg);
            setMovementTarget(null);
        } catch (e: any) {
            toast.error(e.response?.data?.message || 'Failed to send email');
        } finally {
            setMovementSending(false);
        }
    };

    return (
        <div className="ae-page">
            {/* Recipient modal */}
            {pendingAction && (
                <RecipientModal
                    onConfirm={handleConfirm}
                    onCancel={() => !sending && setPendingAction(null)}
                    sending={sending}
                />
            )}

            {/* Movement % modal */}
            {movementTarget && (
                <MovementModal
                    stock={movementTarget}
                    onConfirm={handleMovementConfirm}
                    onCancel={() => !movementSending && setMovementTarget(null)}
                    sending={movementSending}
                />
            )}

            <div className="ae-header">
                <button className="back-btn" onClick={() => navigate(-1)}>
                    <FaArrowLeft />
                </button>
                <h2 className="ae-title">Email Management</h2>
            </div>

            {/* Section 1: Latest stocks */}
            <div className="ae-card">
                <div className="ae-card-header">
                    <span className="ae-card-icon">📨</span>
                    <div>
                        <h3 className="ae-card-title">Latest Captured Stocks</h3>
                        <p className="ae-card-desc">Sends the most recently captured stock count per screener.</p>
                    </div>
                </div>
                <button className="ae-send-btn" onClick={() => openModal({ type: 'new' })}>
                    <FaPaperPlane />
                    Send Latest Stocks Email
                </button>
            </div>

            {/* Section: No stocks captured today */}
            <div className="ae-card">
                <div className="ae-card-header">
                    <span className="ae-card-icon">📭</span>
                    <div>
                        <h3 className="ae-card-title">No Stocks Captured Today</h3>
                        <p className="ae-card-desc">Let users know our screeners didn't find any new stocks today.</p>
                    </div>
                </div>
                <button className="ae-send-btn" onClick={() => openModal({ type: 'noStocks' })}>
                    <FaPaperPlane />
                    Send "No Stocks Today" Email
                </button>
            </div>

            {/* Section 2: Stock list */}
            <div className="ae-card">
                <div className="ae-card-header">
                    <span className="ae-card-icon">📋</span>
                    <div>
                        <h3 className="ae-card-title">Stock Emails</h3>
                        <p className="ae-card-desc">
                            Click <strong>✉</strong> on a row to send a single stock email, or select multiple and use <strong>Send Selected</strong>.
                        </p>
                    </div>
                </div>

                <div className="ae-filters">
                    <select
                        className="ae-select ae-select-sm"
                        value={screenerFilter}
                        onChange={e => { setScreenerFilter(e.target.value); setBulkPage(1); setSelectedIds(new Set()); }}
                    >
                        <option value="ALL">All Screeners</option>
                        {screenerOptions.map(name => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                    <input
                        className="ae-search"
                        type="text"
                        placeholder="Search symbol or name..."
                        value={bulkSearch}
                        onChange={e => { setBulkSearch(e.target.value); setBulkPage(1); }}
                    />
                    <span className="ae-selected-count">{selectedIds.size} selected</span>
                </div>

                <div className="ae-table-wrap">
                    {stocksLoading ? (
                        <div className="ae-loading">Loading stocks...</div>
                    ) : (
                        <table className="ae-table">
                            <thead>
                                <tr>
                                    <th><input type="checkbox" checked={allPageSelected} onChange={toggleSelectAll} /></th>
                                    <th>Symbol</th>
                                    <th>Stock Name</th>
                                    <th>Screener</th>
                                    <th>Captured Price</th>
                                    <th>Max Upside</th>
                                    <th>Captured On</th>
                                    <th>Send</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedStocks.length === 0 ? (
                                    <tr><td colSpan={8} className="ae-empty">No stocks found</td></tr>
                                ) : (
                                    pagedStocks.map(s => (
                                        <tr
                                            key={s.id}
                                            className={selectedIds.has(s.id) ? 'ae-row-selected' : ''}
                                            onClick={() => toggleStock(s.id)}
                                        >
                                            <td onClick={e => e.stopPropagation()}>
                                                <input type="checkbox" checked={selectedIds.has(s.id)} onChange={() => toggleStock(s.id)} />
                                            </td>
                                            <td className="ae-symbol">{s.symbol}</td>
                                            <td>{s.stockName}</td>
                                            <td className="ae-screener">{s.screenerName ?? '—'}</td>
                                            <td>{formatPrice(s.capturedPrice)}</td>
                                            <td>{formatUpside(s.maxUpside)}</td>
                                            <td>{formatDate(s.capturedOn)}</td>
                                            <td className="ae-send-cell" onClick={e => e.stopPropagation()}>
                                                <button
                                                    className="ae-single-btn"
                                                    onClick={() => openModal({ type: 'single', stockId: s.id })}
                                                    title="Send single stock email"
                                                >
                                                    <FaPaperPlane />
                                                </button>
                                                <button
                                                    className="ae-single-btn ae-movement-btn"
                                                    onClick={() => setMovementTarget(s)}
                                                    title="Send single-day movement % email"
                                                >
                                                    <FaPercentage />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {totalPages > 1 && (
                    <div className="ae-pagination">
                        <button className="ae-page-btn" onClick={() => setBulkPage(p => p - 1)} disabled={bulkPage === 1}>Previous</button>
                        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                            const start = Math.max(1, bulkPage - 2);
                            const page = start + i;
                            if (page > totalPages) return null;
                            return (
                                <button key={page} className={`ae-page-btn ${bulkPage === page ? 'active' : ''}`} onClick={() => setBulkPage(page)}>
                                    {page}
                                </button>
                            );
                        })}
                        <button className="ae-page-btn" onClick={() => setBulkPage(p => p + 1)} disabled={bulkPage === totalPages}>Next</button>
                        <span className="ae-page-info">Page {bulkPage} of {totalPages} · {filteredStocks.length} stocks</span>
                    </div>
                )}

                <div className="ae-bulk-footer">
                    <button
                        className="ae-send-btn"
                        onClick={() => openModal({ type: 'bulk', stockIds: [...selectedIds] })}
                        disabled={selectedIds.size === 0}
                    >
                        <FaPaperPlane />
                        Send Selected ({selectedIds.size} Stock{selectedIds.size !== 1 ? 's' : ''})
                    </button>
                </div>
            </div>

            {/* Section: Custom Email */}
            <CustomEmailSection />
        </div>
    );
};

export default AdminEmailPage;
